import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getBaseURL } from "../../../env/stage";
import AtlassianFormBuilder, { getValue } from "../../../formBuilder/AtlassianFormBuilder";
import { authroizationCodeFields, sendAccessTokenReqOptions } from "../../../common/constants/core/oauth2.constants";
import Button from "@atlaskit/button/new";
import Lozenge from "@atlaskit/lozenge";
import { getCreds } from "../../../redux/actions/credActions";
import { toggleLoading } from "../../../redux/actions/messageActions";
import { clone, nullToEmptyString } from "../../../common/utils/helper";
import { modifySelectedCred } from "../../../redux/actions/credActions";
import CopyTextButton from "../../../common/components/CopyTextButton";
import { OAuth2SendAs } from "../../../common/interfaces/credentials/Credentials";

const GrantTypeAuthorizationCodeForm = () => {
  const [values, setValues] = useState([]);
  const selectedCred = useSelector((state) => state.auth.selectedCred);
  const unsaved_cred = useSelector((state) => state.auth.unsaved_cred);
  const CALLBACK_URL = getBaseURL() + "/authorization/oauth2/callback/" + selectedCred.id + "/" + selectedCred.clientKey;
  const dispatch = useDispatch();

  useEffect(() => {
    setValues([
      {
        id: "NAME",
        value: nullToEmptyString(selectedCred.name),
      },
      {
        id: "AUTHORIZE_URL",
        value: nullToEmptyString(selectedCred?.data?.config?.url?.authorizeUrl),
      },
      {
        id: "ACCESS_TOKEN_URL",
        value: nullToEmptyString(selectedCred?.data?.config?.url?.accessTokenUrl),
      },
      {
        id: "CLIENT_ID",
        value: nullToEmptyString(selectedCred?.data?.config?.client?.id),
      },
      {
        id: "CLIENT_SECRET",
        value: nullToEmptyString(selectedCred?.data?.config?.client?.secret),
      },
      {
        id: "SCOPE",
        value: nullToEmptyString(selectedCred?.data?.config?.scope),
      },
      {
        id: "INCLUDE_STATE",
        value: selectedCred?.data?.config?.includeState ? selectedCred?.data?.config?.includeState : false,
      },
      {
        id: "AUTHORIZATION_HEADER_PREFIX",
        value: selectedCred?.data?.config?.headerPrefix ?? "Bearer",
      },
      {
        id: "INCLUDE_SCOPE_IN_ACCESS_TOKEN_REQ",
        value: selectedCred?.data?.config?.includeScopeInAccessTokenReq ? selectedCred?.data?.config?.includeScopeInAccessTokenReq : false,
      },
      {
        id: "MAKE_ACCESS_TOKEN_REQUEST_AS",
        value: selectedCred?.data?.config?.sendParamsAs ? selectedCred?.data?.config?.sendParamsAs : OAuth2SendAs.QUERY_PARAMS,
      },
      {
        id: "URL_ENCODE",
        value: selectedCred?.data?.config?.urlEncode ? selectedCred?.data?.config?.urlEncode : false,
      },
    ]);
  }, [selectedCred]);

  const callback = (extConfigValues) => {
    let credentials = clone(selectedCred);
    credentials.name = extConfigValues.find((v) => v.id === "NAME").value;
    if (!credentials.data) {
      credentials["data"] = {
        config: {
          client: {
            id: "",
            secret: "",
          },
          url: {
            authorizeUrl: "",
            accessTokenUrl: "",
          },
          grantType: "",
          scope: "",
          headerPrefix: "Bearer",
          state: "",
          includeState: true,
          includeScopeInAccessTokenReq: true,
          sendParamsAs: OAuth2SendAs.QUERY_PARAMS,
          urlEncode: false,
        },
      };
    }

    const data = credentials.data ? credentials.data : {};
    const config = credentials.data?.config ? credentials.data.config : {};
    const client = credentials.data?.config?.client ? credentials.data.config.client : {};
    const url = credentials.data?.config?.url ? credentials.data.config.url : {};

    client["id"] = getValue(extConfigValues, "CLIENT_ID");
    client["secret"] = getValue(extConfigValues, "CLIENT_SECRET");
    url["authorizeUrl"] = getValue(extConfigValues, "AUTHORIZE_URL");
    url["accessTokenUrl"] = getValue(extConfigValues, "ACCESS_TOKEN_URL");
    config["scope"] = getValue(extConfigValues, "SCOPE");
    config["headerPrefix"] = getValue(extConfigValues, "AUTHORIZATION_HEADER_PREFIX");
    config["includeState"] = getValue(extConfigValues, "INCLUDE_STATE");
    config["includeScopeInAccessTokenReq"] = getValue(extConfigValues, "INCLUDE_SCOPE_IN_ACCESS_TOKEN_REQ");
    config["sendParamsAs"] = getValue(extConfigValues, "MAKE_ACCESS_TOKEN_REQUEST_AS");
    config["urlEncode"] = getValue(extConfigValues, "URL_ENCODE");

    config["client"] = client;
    config["url"] = url;
    data["config"] = config;
    credentials["data"] = data;

    dispatch(modifySelectedCred(credentials));
  };

  const enableAuthorizeButton = () => {
    return !(
      !unsaved_cred &&
      selectedCred?.data?.config?.url?.authorizeUrl &&
      selectedCred?.data?.config?.url?.accessTokenUrl &&
      selectedCred?.data?.config?.client?.id &&
      selectedCred?.data?.config?.client?.secret
    );
  };

  const authorize = () => {
    dispatch(toggleLoading(true, "authorizing..."));

    const authWindow = window.open(encodeURI(buildUrl()));
    const timer = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(timer);
        dispatch(getCreds());
        dispatch(toggleLoading(false, "authorizing done"));
      }
    }, 1000);
  };

  const buildUrl = () => {
    let url = selectedCred?.data?.config?.url?.authorizeUrl;
    let authorizeData =
      "client_id=" +
      selectedCred?.data?.config?.client?.id +
      "&response_type=" +
      selectedCred?.data?.config?.grantType +
      "&redirect_uri=" +
      CALLBACK_URL;

    if (selectedCred?.data?.config?.scope) authorizeData += "&scope=" + selectedCred?.data?.config?.scope;
    if (selectedCred?.data?.config?.includeState) authorizeData += "&state=" + selectedCred?.data?.config?.state;

    //check if any query params are added to url
    if (selectedCred?.data?.config?.url?.authorizeUrl.includes("?")) {
      url += "&" + authorizeData;
    } else {
      url += "?" + authorizeData;
    }

    return url;
  };

  const authorizationStatus = () => {
    if (selectedCred?.data?.response?.isErrors) {
      return <Lozenge appearance="removed">ERROR AUTHORIZING APP</Lozenge>;
    } else {
      if (selectedCred?.data?.response?.accessToken) {
        return <Lozenge appearance="success">GRANTED</Lozenge>;
      } else {
        return <Lozenge>NOT YET AUTHORIZED</Lozenge>;
      }
    }
  };

  return (
    <>
      <AtlassianFormBuilder fields={authroizationCodeFields} values={values} options={sendAccessTokenReqOptions} update={callback} />
      <br />
      <b>Authorization</b> {authorizationStatus()}
      <br />
      <span>
        Save the form and click the button below to authorize. Once authorized the access + refresh tokens will be saved and this credential
        can be used with one or more integration flows.
      </span>
      <br /> Callback url: <b>{CALLBACK_URL}</b>
      <CopyTextButton copyText={CALLBACK_URL} />
      <br />
      <Button appearance="primary" isDisabled={enableAuthorizeButton()} onClick={authorize}>
        Click here to authorize
      </Button>
      <br />
      <br />
    </>
  );
};

export default GrantTypeAuthorizationCodeForm;
